/**
 * Small pieces the Support screens share: how a kind and a status read, the
 * filter options, tags, and the guard on links the support Worker hands back.
 */
import { useQueryClient } from "@tanstack/react-query";

import { StatusChip, type StatusChipVariant } from "@/components/kit";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { KnowledgeKind } from "@/types-support";

export {
  Failure,
  useIsSessionAdmin,
  useMe,
} from "@/routes/state-filings/shared";
export { NOTE_CLASS } from "@/routes/federal-tax/packet-shared";

const KIND_LABEL: Record<KnowledgeKind, string> = {
  qa: "Q&A",
  page: "Web page",
  site: "Site page",
};

const KIND_VARIANT: Record<KnowledgeKind, StatusChipVariant> = {
  qa: "purple",
  page: "blue",
  site: "green",
};

export function KindBadge({ kind }: { kind: string }) {
  const known = kind in KIND_LABEL ? (kind as KnowledgeKind) : null;
  return (
    <StatusChip variant={known ? KIND_VARIANT[known] : "zinc"}>
      {known ? KIND_LABEL[known] : kind}
    </StatusChip>
  );
}

export function KnowledgeStatusChip({ status }: { status: string }) {
  return (
    <StatusChip variant={status === "active" ? "green" : "zinc"}>
      {status === "active" ? "Active" : "Archived"}
    </StatusChip>
  );
}

export function TagList({ tags }: { tags: readonly string[] | undefined }) {
  if (!tags || tags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge key={tag} variant="zinc" size="xs">
          {tag}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The site an entry came from, shown beside its title: nt-support's `host`,
 * else the source url's host without `www.`. Null for a Q&A.
 */
export function sourceHost(entry: {
  host?: string | null;
  sourceUrl?: string | null;
}): string | null {
  if (entry.host) return entry.host.replace(/^www\./, "");
  if (!entry.sourceUrl) return null;
  try {
    return new URL(entry.sourceUrl).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * A date that fits one line in a narrow column: "Oct 3, 10:50" this year,
 * "Oct 3, 2025" earlier. The full timestamp belongs in the cell's tooltip.
 */
export function formatCompactDate(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  if (date.getFullYear() !== now.getFullYear()) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${day}, ${time}`;
}

export function kindLabel(kind: string): string {
  return kind in KIND_LABEL ? KIND_LABEL[kind as KnowledgeKind] : kind;
}

/**
 * A link only when it is an https address: the support Worker hands back source
 * and citation urls, and a `javascript:` one must never become an href.
 */
function safeHttpsUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value).protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

export function ExternalLink({
  href,
  children,
  title,
  className,
  testId,
}: {
  href: string | null | undefined;
  children: React.ReactNode;
  title?: string;
  className?: string;
  testId?: string;
}) {
  const safe = safeHttpsUrl(href);
  if (!safe) {
    return (
      <span title={title} className={className} data-testid={testId}>
        {children}
      </span>
    );
  }
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      data-testid={testId}
      className={cn(
        "text-primary underline-offset-4 hover:underline",
        className,
      )}
    >
      {children}
    </a>
  );
}

/** After a knowledge write: re-read the lists, the counts and this entry. */
export function useInvalidateKnowledge() {
  const client = useQueryClient();
  return (entryId?: string) =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["support-knowledge"] }),
      client.invalidateQueries({ queryKey: ["support-stats"] }),
      ...(entryId
        ? [
            client.invalidateQueries({
              queryKey: ["support-knowledge-entry", entryId],
            }),
          ]
        : []),
    ]);
}

export function percent(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${Math.round(value * 100)}%`;
}
